import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { EyeCloseIcon, EyeIcon } from "../../icons";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import Button from "../ui/button/Button";
import { auth } from '../../../config/firebase.js';
import { GoogleAuthProvider, signInWithEmailAndPassword, signInWithPopup, unlink } from 'firebase/auth';
import Alert from "../ui/alert/Alert";
import { useAuthError } from "../../hooks/useAuthError";
import { useStateValue } from "../../context/StateValueContext";
import { useApi } from "../../hooks/useApi";
import { requiresGoogleSignIn } from "../../utils/authUtils";

const MESSAGE_CONFIG: Record<string, { title: string; message?: string; variant: 'info' | 'warning' | 'success' }> = {
  password_set: {
    title: 'Password set successfully. Please sign in with your new password.',
    variant: 'success',
  },
  session_expired: {
    title: 'Your session expired. Please sign in again.',
    variant: 'info',
  },
  account_disabled: {
    title: 'Your account has been disabled. Contact support.',
    variant: 'warning',
  },
};

export default function SignInForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState<{ title: string; message?: string; variant: 'info' | 'warning' | 'success' } | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { authError, handleAuthError, clearAuthError } = useAuthError();
  const [{ user }] = useStateValue();
  const { post } = useApi();

  // Handle message query parameter
  useEffect(() => {
    const msgParam = searchParams.get('msg');
    if (msgParam && MESSAGE_CONFIG[msgParam]) {
      setInfoMessage(MESSAGE_CONFIG[msgParam]);
      // Remove the msg parameter from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('msg');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Call post-login and redirect to home if user is already signed in
  useEffect(() => {
    if (user) {
      post('/api/users/post-login', {}, { includeCredentials: true }).catch((err) => {
        console.error('Post-login API call failed:', err);
      });
      navigate('/', { replace: true });
    }
  }, [user, navigate, post]);

  const handleSignIn = async (e: any) => {
    e.preventDefault();
    if (!email) {
      setError("Missing email");
      return;
    }
    if (!password) {
      setError("Missing password");
      return;
    }
    if (requiresGoogleSignIn(email)) {
      setError('@chargemont.com accounts must use Google Sign-In. Please click the "Sign in with Google" button above.');
      return;
    }
    setError('');
    clearAuthError();

    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const idTokenResult = await result.user.getIdTokenResult(true);
      if (idTokenResult.claims.mustChangePassword === true) {
        navigate('/set-password', { replace: true });
        return;
      }
      // Don't navigate here - let the useEffect handle it when user state updates.
      // post-login is dispatched from that useEffect.
    } catch (e) {
      handleAuthError(e);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const googleUser = result.user;

      // Check if this is a new user who needs onboarding
      const idTokenResult = await googleUser.getIdTokenResult(true);
      if (idTokenResult.claims.mustChangePassword === true) {
        await post('/api/users/complete-google-onboarding', {});
        // Refresh token so post-login sees clean claims
        await googleUser.getIdTokenResult(true);
        post('/api/users/post-login', {}, { includeCredentials: true }).catch((err) => {
          console.error('Post-login API call failed:', err);
        });
        // Reload — onAuthStateChanged will pick up clean claims
        window.location.href = '/';
        return;
      }

      const hasPassword = googleUser.providerData.some(p => p.providerId === 'password');
      if (hasPassword) {
        await unlink(googleUser, 'password');
      }
      // post-login is dispatched from the redirect useEffect when user state updates.
    } catch (err) {
      handleAuthError(err);
    }
  };

  return (
    <div className="flex flex-col flex-1">
      {/* <div className="w-full max-w-md pt-10 mx-auto">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
        >
          <ChevronLeftIcon className="size-5" />
          Back to dashboard
        </Link>
      </div> */}
      <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
        <div>
          <div className="mb-5 sm:mb-8">
            <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
              Sign In
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Enter your email and password to sign in!
            </p>
          </div>
          {infoMessage && (
            <div className="mb-5">
              <Alert
                variant={infoMessage.variant}
                title={infoMessage.title}
                message={infoMessage.message}
                showLink={false}
              />
            </div>
          )}
          <div>
            <div className="grid grid-cols-1 gap-3 sm:gap-5">
              <button onClick={handleGoogleLogin} className="inline-flex items-center justify-center gap-3 py-3 text-sm font-normal text-gray-700 transition-colors bg-gray-100 rounded-lg px-7 hover:bg-gray-200 hover:text-gray-800 dark:bg-white/5 dark:text-white/90 dark:hover:bg-white/10 w-full">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18.7511 10.1944C18.7511 9.47495 18.6915 8.94995 18.5626 8.40552H10.1797V11.6527H15.1003C15.0011 12.4597 14.4654 13.675 13.2749 14.4916L13.2582 14.6003L15.9087 16.6126L16.0924 16.6305C17.7788 15.1041 18.7511 12.8583 18.7511 10.1944Z"
                    fill="#4285F4"
                  />
                  <path
                    d="M10.1788 18.75C12.5895 18.75 14.6133 17.9722 16.0915 16.6305L13.274 14.4916C12.5201 15.0068 11.5081 15.3666 10.1788 15.3666C7.81773 15.3666 5.81379 13.8402 5.09944 11.7305L4.99473 11.7392L2.23868 13.8295L2.20264 13.9277C3.67087 16.786 6.68674 18.75 10.1788 18.75Z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.10014 11.7305C4.91165 11.186 4.80257 10.6027 4.80257 9.99992C4.80257 9.3971 4.91165 8.81379 5.09022 8.26935L5.08523 8.1534L2.29464 6.02954L2.20333 6.0721C1.5982 7.25823 1.25098 8.5902 1.25098 9.99992C1.25098 11.4096 1.5982 12.7415 2.20333 13.9277L5.10014 11.7305Z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M10.1789 4.63331C11.8554 4.63331 12.9864 5.34303 13.6312 5.93612L16.1511 3.525C14.6035 2.11528 12.5895 1.25 10.1789 1.25C6.68676 1.25 3.67088 3.21387 2.20264 6.07218L5.08953 8.26943C5.81381 6.15972 7.81776 4.63331 10.1789 4.63331Z"
                    fill="#EB4335"
                  />
                </svg>
                Sign in with Google
              </button>

              {/* <button className="inline-flex items-center justify-center gap-3 py-3 text-sm font-normal text-gray-700 transition-colors bg-gray-100 rounded-lg px-7 hover:bg-gray-200 hover:text-gray-800 dark:bg-white/5 dark:text-white/90 dark:hover:bg-white/10">
                <svg
                  width="21"
                  className="fill-current"
                  height="20"
                  viewBox="0 0 21 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M15.6705 1.875H18.4272L12.4047 8.75833L19.4897 18.125H13.9422L9.59717 12.4442L4.62554 18.125H1.86721L8.30887 10.7625L1.51221 1.875H7.20054L11.128 7.0675L15.6705 1.875ZM14.703 16.475H16.2305L6.37054 3.43833H4.73137L14.703 16.475Z" />
                </svg>
                Sign in with X
              </button> */}
            </div>
            <div className="relative py-3 sm:py-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-800"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="p-2 text-gray-400 bg-white dark:bg-gray-900 sm:px-5 sm:py-2">
                  Or
                </span>
              </div>
            </div>
            <form onSubmit={handleSignIn}>
              <div className="space-y-6">
                <div>
                  <Label>
                    Email <span className="text-error-500">*</span>{" "}
                  </Label>
                  <Input
                    placeholder="info@gmail.com"
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div>
                  <Label>
                    Password <span className="text-error-500">*</span>{" "}
                  </Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                    >
                      {showPassword ? (
                        <EyeIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                      ) : (
                        <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                      )}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Link
                    to="/reset-password"
                    className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div>
                  {error && (
                    <Alert
                      variant="error"
                      title={error}
                      showLink={false}
                    />
                  )}
                </div>
                <div>
                  {!error && authError && (
                    <Alert
                      variant="error"
                      title={authError}
                      showLink={false}
                    />
                  )}
                </div>
                <div>
                  <Button className="w-full" size="sm">
                    Sign in
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
